import type { ICourseHit } from '@/types/search'
import { ComponentProps, useLayoutEffect } from 'react'
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch'
import { useIntersectionObserver } from 'usehooks-ts'
import { NewCourseCard } from '../../course/components/NewCourseCard'
import { useLanguageSelection } from '../../shared/providers/LanguageSelectionProvider'
import { adaptCourseCollectionToCourseCard } from '../lib/course-adapter'
import { NoResultsState } from './NoResultsState'
import { SearchErrorState } from './SearchErrorState'
import { SearchLoadingState } from './SearchLoadingState'

type InfiniteCourseHitsProps = ComponentProps<'div'>

export function InfiniteCourseHits(props: InfiniteCourseHitsProps) {
  const { items = [], isLastPage, showMore } = useInfiniteHits<ICourseHit>()
  const { status, refresh, setUiState } = useInstantSearch()
  const { currentLocale } = useLanguageSelection()

  const isStalled = status === 'stalled'
  const isLoading = status === 'loading'
  const isError = status === 'error'

  const { isIntersecting: isVisible, ref } = useIntersectionObserver({
    initialIsIntersecting: false,
  })

  useLayoutEffect(() => {
    if (status !== 'idle' || isLastPage || !isVisible) {
      return
    }

    showMore()
  }, [isVisible, isLastPage, status, showMore])

  const handleErrorRetry = () => {
    refresh()
  }

  const handleNoResultsRetry = () => {
    setUiState({})
  }

  return (
    <>
      {isError ? (
        <SearchErrorState
          onRetry={handleErrorRetry}
          className="md:min-h-[65vh]"
        />
      ) : (isLoading || isStalled) && !items.length ? (
        <SearchLoadingState {...props} />
      ) : !isLoading && !items.length ? (
        <NoResultsState
          onRetry={handleNoResultsRetry}
          className="md:min-h-[65vh]"
        />
      ) : (
        <>
          <div {...props}>
            {items.map((hit) => (
              <NewCourseCard
                key={hit.id}
                course={adaptCourseCollectionToCourseCard<typeof currentLocale>(
                  hit,
                  currentLocale
                )}
              />
            ))}
            {(isLoading || isStalled || !items.length) && (
              <SearchLoadingState {...props} bare />
            )}
          </div>
        </>
      )}
      {items.length && <div ref={ref} aria-hidden="true" />}
    </>
  )
}
